'use client'

import { sortSizesFromStrings } from '@/lib/helpers/sort-size.helper'
import { __cContactService } from '@/services/client/__c.contact.service'
import { ProductFormatted } from '@/types/product'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

export type Step = 'PRODUCT' | 'FORM' | 'SUCCESS'

interface UseBirthdayPreOrderProps {
  product: ProductFormatted
}

export const useBirthdayPreOrder = ({ product }: UseBirthdayPreOrderProps) => {
  const [step, setStep] = useState<Step>('PRODUCT')
  const colors = useMemo(() => {
    if (!product?.mapped_variants) return []
    return Object.keys(product.mapped_variants).filter((c) =>
      ['Đen'].includes(c)
    )
  }, [product])

  const [selectedColor, setSelectedColor] = useState<string>(() => {
    // Luôn chọn màu đầu tiên có hàng
    const colorWithStock = colors.find((c) =>
      product.mapped_variants[c]?.some((v) => !v.isDisable)
    )
    return colorWithStock || colors[0] || ''
  })

  const [selectedSize, setSelectedSize] = useState<string>('')
  const [customName, setCustomName] = useState<string>('')
  const [nameError, setNameError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onCustomNameChange = useCallback((name: string) => {
    setCustomName(name)
    if (name.trim()) {
      setNameError('')
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    defaultValues: {
      quantity_product: '',
      name: '',
      phone: '',
      email: '',
    },
  })

  const sizes = useMemo(() => {
    if (!product?.mapped_variants || !selectedColor) return []
    const variants = product.mapped_variants[selectedColor] || []

    // Create map of size info including stock status
    const sizeInfo = variants.map((v) => ({
      size: v.size || '',
      isDisable: v.isDisable,
    }))

    // Sort sizes while keeping their object structure
    const sortedSizesStrings = sortSizesFromStrings(sizeInfo.map((s) => s.size))

    return sortedSizesStrings.map((sizeStr) => {
      const info = sizeInfo.find((s) => s.size === sizeStr)
      return {
        size: sizeStr,
        isDisable: info?.isDisable || false,
      }
    })
  }, [product, selectedColor])

  // Tự động cập nhật selectedSize khi đổi màu hoặc danh sách sizes thay đổi
  useEffect(() => {
    if (!sizes.length) return

    const isCurrentSizeAvailable = sizes.find(
      (s) => s.size === selectedSize && !s.isDisable
    )

    if (!isCurrentSizeAvailable || !selectedSize) {
      const firstAvailable = sizes.find((s) => !s.isDisable)
      const nextSize = firstAvailable ? firstAvailable.size : sizes[0]?.size
      if (nextSize && nextSize !== selectedSize) {
        setSelectedSize(nextSize)
      }
    }
  }, [selectedColor, sizes, selectedSize])

  const handleNextToForm = useCallback(() => {
    if (!customName.trim()) {
      setNameError('Vui lòng nhập tên của bạn')
      return
    }
    if (!selectedSize) {
      alert('Vui lòng chọn size')
      return
    }
    setStep('FORM')
  }, [selectedSize, customName])

  const handleBackToProduct = useCallback(() => {
    setStep('PRODUCT')
  }, [])

  const handleClosePopup = useCallback(() => {
    setStep('PRODUCT')
  }, [])

  const onFormSubmit = useCallback(
    async (formData: Record<string, string>) => {
      try {
        setIsSubmitting(true)
        const payload = {
          ...formData,
          product: product?.title,
          product_id: product?.id,
          color: selectedColor,
          size: selectedSize,
          customName: customName,
        }

        await __cContactService.submitContactForm(payload)

        setStep('SUCCESS')
      } catch (error) {
        console.error('Failed to submit pre-order:', error)
        // Optionally handle error (e.g., show a toast/alert)
        alert('Có lỗi xảy ra khi gửi thông tin. Vui lòng thử lại sau.')
      } finally {
        setIsSubmitting(false)
      }
    },
    [product, selectedColor, selectedSize, customName]
  )

  return {
    step,
    setStep,
    selectedColor,
    setSelectedColor,
    selectedSize,
    setSelectedSize,
    customName,
    onCustomNameChange,
    colors,
    sizes,
    register,
    handleSubmit,
    errors,
    nameError,
    isSubmitting,
    handleNextToForm,
    handleBackToProduct,
    handleClosePopup,
    onFormSubmit,
  }
}
